'use client';
import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Check, Zap, Star, Building2, Shield, HelpCircle, ChevronDown } from 'lucide-react';
import { useAuthStore } from '@/store/auth.store';

const PLANS = [
  { key: 'free', name: 'Бесплатный', monthPrice: 0, yearPrice: 0, iconType: 'shield', color: 'text-gray-500', bg: 'bg-bg-muted', border: 'border-border', popular: false,
    features: ['До 3 активных услуг', 'Расписание на 7 дней', 'До 5 фото портфолио', '3 отклика в месяц', 'Базовый приоритет в поиске'],
    cta: 'Начать бесплатно' },
  { key: 'start', name: 'Старт', monthPrice: 990, yearPrice: 9504, iconType: 'zap', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', popular: false,
    features: ['До 10 активных услуг', 'Расписание на 30 дней', 'До 20 фото портфолио', '15 откликов в месяц', 'Аналитика за 7 дней', 'Повышенный приоритет', 'Бейдж «Проверен»'],
    cta: 'Подключить Старт' },
  { key: 'pro', name: 'Про', monthPrice: 1990, yearPrice: 19104, iconType: 'star', color: 'text-brand', bg: 'bg-brand/10', border: 'border-brand', popular: true,
    features: ['Неограниченно услуг', 'Расписание на 60 дней', 'До 50 фото портфолио', 'Неограниченно откликов', 'Аналитика за 30 дней', 'Высокий приоритет', 'Бейдж «Про»'],
    cta: 'Подключить Про' },
  { key: 'business', name: 'Бизнес', monthPrice: 3490, yearPrice: 33504, iconType: 'building', color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200', popular: false,
    features: ['Всё из тарифа Про', 'Расписание на 90 дней', 'Неограниченно фото', 'Аналитика за 90 дней', 'Максимальный приоритет', 'Бейдж «Бизнес»'],
    cta: 'Подключить Бизнес' },
];

const TABLE_ROWS = [
  { label: 'Активные услуги', values: ['3', '10', '∞', '∞'] },
  { label: 'Горизонт расписания', values: ['7 дней', '30 дней', '60 дней', '90 дней'] },
  { label: 'Фото портфолио', values: ['5', '20', '50', '∞'] },
  { label: 'Откликов в месяц', values: ['3', '15', '∞', '∞'] },
  { label: 'Аналитика', values: ['—', '7 дней', '30 дней', '90 дней'] },
  { label: 'Приоритет в поиске', values: ['Базовый', 'Повышенный', 'Высокий', 'Максимальный'] },
  { label: 'Бейдж на профиле', values: ['—', 'Проверен', 'Про', 'Бизнес'] },
];

const FAQ = [
  { q: 'Могу ли я сменить тариф в любое время?', a: 'Да, вы можете перейти на более высокий тариф в любой момент из личного кабинета. Переход на более низкий тариф происходит после окончания текущего периода.' },
  { q: 'Как работает бесплатный план?', a: 'Бесплатный план доступен всем зарегистрированным специалистам без ограничений по времени.' },
  { q: 'Что будет если не продлить подписку?', a: 'Аккаунт автоматически переходит на бесплатный план. Данные сохраняются.' },
  { q: 'Есть ли скидка при оплате на год?', a: 'Да! При оплате на год вы экономите 20% по сравнению с ежемесячной оплатой.' },
  { q: 'Как происходит оплата?', a: 'Оплата через ЮKassa. Принимаем Visa, MasterCard, МИР, СБП.' },
];

function PlanIcon({ type, size }: { type: string; size: number }) {
  if (type === 'shield') return <Shield size={size} />;
  if (type === 'zap') return <Zap size={size} />;
  if (type === 'star') return <Star size={size} />;
  return <Building2 size={size} />;
}

function getBgColor(key: string, popular: boolean): string {
  if (key === 'free') return '';
  if (popular) return 'var(--color-brand)';
  if (key === 'start') return 'var(--color-info)';
  return '#9333ea';
}

export default function PricingClient() {
  const [period, setPeriod] = useState<'month' | 'year'>('month');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const router = useRouter();
  const { isAuthenticated } = useAuthStore();

  const handleCta = (planKey: string) => {
    if (planKey === 'free') { router.push('/register'); return; }
    if (!isAuthenticated()) { router.push('/login'); return; }
    router.push('/dashboard?tab=subscription');
  };

  return (
    <div className="min-h-screen bg-bg-subtle">
      <div className="bg-bg-surface border-b border-border py-14 px-4 text-center">
        <h1 className="text-3xl sm:text-4xl font-bold text-text-primary mb-3">Тарифы для специалистов</h1>
        <p className="text-gray-500 text-lg max-w-xl mx-auto">Начните бесплатно. Подключите подписку когда будете готовы расти.</p>
        <div className="flex items-center justify-center mt-8">
          <div className="flex items-center gap-1 bg-bg-muted rounded-xl p-1">
            <button onClick={() => setPeriod('month')} className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${period === 'month' ? 'bg-bg-surface text-text-primary shadow-sm' : 'text-gray-500'}`}>Месяц</button>
            <button onClick={() => setPeriod('year')} className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${period === 'year' ? 'bg-bg-surface text-text-primary shadow-sm' : 'text-gray-500'}`}>Год <span className="text-green-600 font-semibold">−20%</span></button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PLANS.map((plan) => {
            const price = plan.monthPrice === 0 ? 0 : period === 'month' ? plan.monthPrice : Math.round(plan.yearPrice / 12);
            const bgColor = getBgColor(plan.key, plan.popular);
            return (
              <div key={plan.key} className={`bg-bg-surface rounded-2xl border-2 p-6 relative flex flex-col ${plan.popular ? plan.border : 'border-border'}`}>
                {plan.popular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-brand text-white text-xs font-semibold px-4 py-1 rounded-full whitespace-nowrap">Популярный</div>
                )}
                <div className={`w-11 h-11 rounded-xl ${plan.bg} flex items-center justify-center ${plan.color} mb-4`}>
                  <PlanIcon type={plan.iconType} size={22} />
                </div>
                <h2 className="font-bold text-text-primary text-xl mb-1">{plan.name}</h2>
                <div className="flex items-baseline gap-1 mb-1">
                  {price === 0 ? (
                    <span className="text-2xl font-bold text-text-primary">Бесплатно</span>
                  ) : (
                    <>
                      <span className="text-2xl font-bold text-text-primary">{price.toLocaleString('ru-RU')} ₽</span>
                      <span className="text-gray-400 text-sm">/мес</span>
                    </>
                  )}
                </div>
                {period === 'year' && plan.yearPrice > 0 ? (
                  <p className="text-xs text-green-600 mb-4">{plan.yearPrice.toLocaleString('ru-RU')} ₽/год</p>
                ) : (
                  <div className="mb-4" />
                )}
                <ul className="space-y-2.5 mb-6 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-text-secondary">
                      <Check size={15} className="text-green-500 mt-0.5 flex-shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                {plan.key === 'free' ? (
                  <button onClick={() => handleCta(plan.key)} className="w-full py-3 rounded-xl text-sm font-semibold bg-bg-muted text-text-ui hover:bg-gray-200 transition-colors">{plan.cta}</button>
                ) : (
                  <button onClick={() => handleCta(plan.key)} className="w-full py-3 rounded-xl text-sm font-semibold text-white hover:opacity-90 transition-opacity" style={{ backgroundColor: bgColor }}>{plan.cta}</button>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-16">
          <h2 className="text-2xl font-bold text-text-primary text-center mb-8">Подробное сравнение тарифов</h2>
          <div className="bg-bg-surface rounded-2xl border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-4 font-semibold text-text-ui w-1/3">Возможность</th>
                    {PLANS.map((p) => <th key={p.key} className={`p-4 font-semibold text-center ${p.color}`}>{p.name}</th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {TABLE_ROWS.map((row) => (
                    <tr key={row.label} className="hover:bg-bg-subtle">
                      <td className="p-4 text-text-ui font-medium">{row.label}</td>
                      {row.values.map((v, i) => (
                        <td key={i} className="p-4 text-center text-text-secondary">{v === '—' ? <span className="text-gray-300">—</span> : v}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="mt-16 max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-text-primary text-center mb-8">Часто задаваемые вопросы</h2>
          <div className="space-y-3">
            {FAQ.map((item, i) => (
              <div key={i} className="bg-bg-surface rounded-2xl border border-border overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
                  <span className="font-medium text-text-primary pr-4">{item.q}</span>
                  <ChevronDown size={18} className={`text-gray-400 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && <div className="px-5 pb-5 text-sm text-text-secondary leading-relaxed">{item.a}</div>}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 text-center bg-bg-surface rounded-2xl border border-border py-12 px-6">
          <HelpCircle size={32} className="text-brand mx-auto mb-4" />
          <h3 className="text-xl font-bold text-text-primary mb-2">Остались вопросы?</h3>
          <p className="text-gray-500 mb-6">Напишите нам — поможем выбрать подходящий тариф</p>
          <a href="mailto:support@svoyprofi.ru" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white text-sm font-semibold bg-brand hover:opacity-90 transition-opacity">Написать в поддержку</a>
        </div>
      </div>
    </div>
  );
}
